import { useId, useState } from 'react';

import { useTranslation } from '@/i18n/useTranslation';

export interface JobDescriptionProps {
  /** Already sanitised server-side on an allow-list [KJM-3 § Invariant]. Never sanitised again here. */
  readonly html: string;
}

/**
 * The published description, behind the accordion the frame draws [KJM-3; Figma `Accordion` 12021:40793].
 *
 * **Rendered whole and verbatim** [decisions 0051, 0072]. No semantic parsing, no splitting into sections,
 * no normalisation across jobs — Rusty: *"low touch, low interpretation on it. Just try to show it as is,
 * rich text."*
 *
 * **ISS's EEOC and Use-of-AI boilerplate is part of this string, and the kiosk never appends it** [0072].
 * That is why nothing here composes a footer: Heidi offered exactly that — hold the static verbiage
 * separately and pull in portions of the description — and Rootstrap declined, because appending it would
 * quietly transfer maintenance of ISS's legal copy to us. Its absence from a rendered description means the
 * wrong field was read or the text was trimmed, which is the diagnostic `eval-kjm-8` exists to fire.
 *
 * > **A note on the frame, so the difference is not read as a mistake.** `08 · Job detail` draws a paragraph
 * > ABOVE the accordion and the two boilerplate sentences BELOW it. All three are the same field: the mock
 * > shows the body with the control sitting in the middle of it. Composing that layout literally would mean
 * > authoring the tail ourselves, which 0072 forbids — so the whole field renders in one block when opened.
 *
 * **It starts COLLAPSED** [Operator, 2026-08-31]. `08 · Job detail` draws the description paragraph ABOVE
 * the control and the two boilerplate sentences BELOW it — head and tail of one field with the control
 * sitting between them. Implemented literally that would mean authoring the tail ourselves, which 0072
 * forbids in as many words, so the whole field renders in one block with the control above it.
 *
 * That block starts closed. An earlier version started it open, reasoning that a screen built to show the
 * description should not ask for a second tap — but ISS's real descriptions are not the two paragraphs that
 * reasoning assumed. The live order this was checked against carries 3,928 characters, the EEOC paragraph
 * included, which pushes the shift, the pay and the button that applies for the job off the screen entirely.
 * A candidate cannot act on what they cannot see, and the summary rows above are what they came to read
 * first. Opening it is one tap, and it is now a tap somebody chooses.
 *
 * `dangerouslySetInnerHTML` is the point of this component rather than a shortcut. It is confined here, the
 * string reaching it was allow-listed by `sanitize-html` in the backend, and the alternative — parsing to
 * React elements on the client — is a second sanitiser with a second set of gaps.
 */
export function JobDescription({ html }: JobDescriptionProps) {
  const t = useTranslation();
  // Closed on arrival — see the note above: a real ISS description is long enough to bury the summary rows
  // and the apply button beneath it.
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="w-full">
      {/*
        An inset ring, not a `border`. The frame draws `Accordion` as 448x44 with the stroke ON the box, so
        a real border made it 46 — the same two pixels that cost `ArrivalOptionButton` twelve across six
        cards, and here they land straight on the footer's position.
      */}
      <button
        aria-controls={panelId}
        aria-expanded={open}
        className="kiosk-focusable flex w-full items-center justify-between rounded-[var(--radius-sm)] px-4 py-3 shadow-[inset_0_0_0_1px_var(--divider-border)]"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        type="button"
      >
        <span className="text-sm font-bold leading-5 text-[var(--foreground)]">
          {t('jobs.detail.fullDescription')}
        </span>
        {/*
          A chevron that rotates rather than two glyphs, so the control's state is visible without a second
          asset. `aria-hidden` because `aria-expanded` on the button already carries the state.
        */}
        <svg
          aria-hidden="true"
          className={`size-5 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="var(--foreground)"
          strokeLinecap="round"
          strokeWidth="2"
          viewBox="0 0 20 20"
        >
          <path d="m5 7.5 5 5 5-5" />
        </svg>
      </button>

      {open && (
        /*
          `kiosk-rich-text` carries the paragraph and list rhythm the frame draws — 16px/24 body, black, with
          24px between blocks, and `mt-6` is that same 24 between the control and the first block: the frame
          spaces everything in this column at 24, and `mt-3` here was the only gap that was not. It lives in `index.css` rather than here because the classes it styles come
          from ISS's own markup and cannot be reached with utilities.

          No max-height and no popup: KJM-3's definition of done says a long description SCROLLS, and 0051
          deferred the popup. The page scrolls, which is what the frames draw — the card simply grows.
        */
        <div
          className="kiosk-rich-text mt-6 w-full text-base leading-6 text-black"
          dangerouslySetInnerHTML={{ __html: html }}
          id={panelId}
        />
      )}
    </div>
  );
}
